import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CreditCard, CheckCircle, XCircle, Loader2, Shield, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [paymentInfo, setPaymentInfo] = useState(null);

  const invoiceId = searchParams.get("invoice") || searchParams.get("invoice_id");
  const devisId = searchParams.get("devis") || searchParams.get("devis_id");
  const paymentType = searchParams.get("type") || searchParams.get("payment_type") || "acompte";

  useEffect(() => {
    // Load payment info
    const loadInfo = async () => {
      try {
        // Use public endpoint for payment info
        const params = new URLSearchParams();
        if (invoiceId) params.append("invoice_id", invoiceId);
        if (devisId) params.append("devis_id", devisId);
        params.append("payment_type", paymentType);
        
        const res = await axios.get(`${API}/payments/info?${params}`);
        setPaymentInfo(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, [invoiceId, devisId, paymentType]);

  const handlePayment = async () => {
    setProcessing(true);
    setError("");
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        invoice_id: invoiceId || null,
        devis_id: devisId || null,
        payment_type: paymentType,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la création du paiement");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4A843]" />
      </div>
    );
  }

  return (
    <div className="font-sans bg-gray-50 min-h-screen">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white shadow-xl rounded-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1B2E4B] to-[#2d4a6f] p-6 text-center">
              <CreditCard className="w-12 h-12 text-[#D4A843] mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white">Paiement sécurisé</h1>
              <p className="text-white/70 text-sm mt-1">RM Location Prestige</p>
            </div>

            {/* Content */}
            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <XCircle className="text-red-500 flex-shrink-0" size={20} />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {paymentInfo && (
                <>
                  {/* Payment details */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h2 className="font-semibold text-gray-900 mb-3">Détails du paiement</h2>
                    <div className="space-y-2 text-sm">
                      {paymentInfo.clientName && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Client</span>
                          <span className="font-medium">{paymentInfo.clientName}</span>
                        </div>
                      )}
                      {paymentInfo.devisNumero && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Référence</span>
                          <span className="font-medium">{paymentInfo.devisNumero}</span>
                        </div>
                      )}
                      {paymentInfo.vehicleName && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Véhicule</span>
                          <span className="font-medium">{paymentInfo.vehicleName}</span>
                        </div>
                      )}
                      {paymentInfo.dateMarriage && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Date</span>
                          <span className="font-medium">{paymentInfo.dateMarriage}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type</span>
                        <span className="font-medium capitalize">
                          {paymentInfo.type === "acompte" ? "Acompte" : "Solde"}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="text-gray-900 font-semibold">Montant à payer</span>
                        <span className="text-2xl font-bold text-[#D4A843]">{paymentInfo.amount}€</span>
                      </div>
                    </div>
                  </div>

                  {/* Security badges */}
                  <div className="flex items-center justify-center gap-4 mb-6 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Lock size={14} />
                      <span>SSL Sécurisé</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Shield size={14} />
                      <span>Paiement Stripe</span>
                    </div>
                  </div>

                  {/* Pay button */}
                  <button
                    onClick={handlePayment}
                    disabled={processing}
                    data-testid="pay-button"
                    className="w-full py-4 bg-[#D4A843] text-white font-bold text-lg rounded-lg hover:bg-[#c49933] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Redirection vers Stripe...
                      </>
                    ) : (
                      <>
                        <CreditCard size={20} />
                        Payer {paymentInfo.amount}€
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center mt-4">
                    Vous serez redirigé vers la page de paiement sécurisée Stripe
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Success page component
export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get("session_id");

  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(res.data);
      
      if (res.data.payment_status === "paid") {
        setStatus("success");
      } else if (res.data.status === "expired") {
        setStatus("expired");
      } else {
        // Still processing, poll again
        setTimeout(checkStatus, 2000);
      }
    } catch (err) {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return (
    <div className="font-sans bg-gray-50 min-h-screen">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          {status === "loading" && (
            <div className="bg-white shadow-xl rounded-lg p-12">
              <Loader2 className="w-16 h-16 animate-spin text-[#D4A843] mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900">Vérification du paiement...</h1>
              <p className="text-gray-500 mt-2">Veuillez patienter</p>
            </div>
          )}

          {status === "success" && (
            <div className="bg-white shadow-xl rounded-lg p-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Paiement réussi !</h1>
              <p className="text-gray-500 mb-6">
                Merci pour votre paiement de <strong>{paymentInfo?.amount}€</strong>
              </p>
              <p className="text-sm text-gray-400">
                Vous recevrez un email de confirmation sous peu.
              </p>
              <a href="/" className="inline-block mt-8 px-6 py-3 bg-[#1B2E4B] text-white font-semibold rounded hover:bg-[#162440] transition-colors">
                Retour à l'accueil
              </a>
            </div>
          )}

          {status === "expired" && (
            <div className="bg-white shadow-xl rounded-lg p-12">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Session expirée</h1>
              <p className="text-gray-500 mb-6">
                Votre session de paiement a expiré. Veuillez réessayer.
              </p>
              <a href="/" className="inline-block mt-4 px-6 py-3 bg-[#D4A843] text-white font-semibold rounded hover:bg-[#c49933] transition-colors">
                Retour à l'accueil
              </a>
            </div>
          )}

          {status === "error" && (
            <div className="bg-white shadow-xl rounded-lg p-12">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h1>
              <p className="text-gray-500 mb-6">
                Une erreur est survenue lors de la vérification de votre paiement.
              </p>
              <a href="/" className="inline-block mt-4 px-6 py-3 bg-[#1B2E4B] text-white font-semibold rounded hover:bg-[#162440] transition-colors">
                Retour à l'accueil
              </a>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Cancel page component
export function PaymentCancelPage() {
  return (
    <div className="font-sans bg-gray-50 min-h-screen">
      <Navbar />
      <div className="pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="bg-white shadow-xl rounded-lg p-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Paiement annulé</h1>
            <p className="text-gray-500 mb-6">
              Votre paiement a été annulé. Aucun montant n'a été débité.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/" className="px-6 py-3 bg-[#1B2E4B] text-white font-semibold rounded hover:bg-[#162440] transition-colors">
                Retour à l'accueil
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
